import React, { useContext, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { COLORS } from "../../assets/colors";
import { IconButton } from "@material-ui/core";
import { FaMinus, FaPlus } from "react-icons/fa";
import {
  MdAirlineSeatReclineExtra,
  MdEventSeat,
  MdLocationOn,
  MdCalendarToday,
  MdAccessTime,
  MdMyLocation,
} from "react-icons/md";
import moment from "moment";
import PayNowButton from "../../components/UI/Paynow";
import { GlobalContext } from "../../contexts/globalContext";
import CancelButton from "../../components/UI/CancelButton";
import configMerged from "../../config";
import CustomButton from "../../components/UI/Button";
import CircularLoader from "../../components/UI/Loader";

function Summary({ ride, submitPayment, sendFreeRequest, loading }) {
  const navigate = useNavigate();
  const { searchFields, setSearchFields } = useContext(GlobalContext);

  const hasSeparatePricing =
    ride?.front_seat_price != null || ride?.back_seat_price != null;

  const frontPrice = ride?.front_seat_price || ride?.ticket || 0;
  const backPrice = ride?.back_seat_price || ride?.ticket || 0;

  const frontSeatsTotal = ride?.front_seats_total ?? 1;
  const frontSeatsBooked = ride?.front_seats_booked ?? 0;
  const frontAvailable = Math.max(0, frontSeatsTotal - frontSeatsBooked);

  const backSeatsTotal =
    ride?.back_seats_total ?? Math.max(0, (ride?.total_seats || 4) - frontSeatsTotal);
  const backSeatsBooked = ride?.back_seats_booked ?? 0;
  const backAvailable = Math.max(0, backSeatsTotal - backSeatsBooked);

  const initialReq = parseInt(searchFields?.seats || 1);

  const [frontSeatsCount, setFrontSeatsCount] = React.useState(0);
  const [backSeatsCount, setBackSeatsCount] = React.useState(() => {
    return Math.min(initialReq, backAvailable > 0 ? backAvailable : initialReq);
  });

  const totalSelectedSeats = frontSeatsCount + backSeatsCount;

  useEffect(() => {
    if (totalSelectedSeats > 0) {
      setSearchFields((prev) => ({ ...prev, seats: totalSelectedSeats }));
    }
  }, [totalSelectedSeats, setSearchFields]);

  const driverCashPayment =
    frontSeatsCount * frontPrice + backSeatsCount * backPrice;

  const commissionPercentage = 7;
  const serviceChargesPercentage = 3;
  const gosawariCommission = Math.ceil(
    (driverCashPayment * commissionPercentage) / 100
  );
  const serviceCharges = Math.ceil(
    (driverCashPayment * serviceChargesPercentage) / 100
  );
  const totalAmountToPay = serviceCharges + gosawariCommission;

  const seatDetailsPayload = {
    front_seats: frontSeatsCount,
    back_seats: backSeatsCount,
    seats: Math.max(1, totalSelectedSeats),
    seat_type:
      frontSeatsCount > 0 && backSeatsCount > 0
        ? "custom"
        : frontSeatsCount > 0
        ? "front"
        : "back",
  };

  const handleIncreaseFront = () => {
    if (frontSeatsCount < frontAvailable) {
      setFrontSeatsCount((prev) => prev + 1);
    }
  };

  const handleDecreaseFront = () => {
    if (frontSeatsCount > 0 && totalSelectedSeats > 1) {
      setFrontSeatsCount((prev) => prev - 1);
    }
  };

  const handleIncreaseBack = () => {
    if (backSeatsCount < backAvailable) {
      setBackSeatsCount((prev) => prev + 1);
    }
  };

  const handleDecreaseBack = () => {
    if (backSeatsCount > 0 && totalSelectedSeats > 1) {
      setBackSeatsCount((prev) => prev - 1);
    }
  };

  if (loading) {
    return (
      <LoadingWrapper>
        <CircularLoader isLoading={loading} />
      </LoadingWrapper>
    );
  }

  return (
    <SummaryCard>
      {/* Cash Banner */}
      <CashBanner>
        <div className="cash-icon">💵</div>
        <div className="cash-content">
          <span className="cash-label">Cash Payment to Driver</span>
          <span className="cash-amount">Rs. {driverCashPayment.toLocaleString()}</span>
        </div>
        <div className="seats-pill">{totalSelectedSeats} seat{totalSelectedSeats !== 1 && "s"}</div>
      </CashBanner>

      {/* Info Text */}
      <InfoText>
        {configMerged.featureFlags.freeLaunch
          ? "🎉 For a limited time, we're not charging the platform fee. Enjoy this special offer!"
          : "We charge only the service commission. Please bring the exact change during the ride."}
      </InfoText>

      {/* Seat Selection Section */}
      <SectionLabel>
        <MdEventSeat />
        Choose Your Seats
      </SectionLabel>

      <SeatGrid>
        <SeatCard $isActive={frontSeatsCount > 0} $soldOut={frontAvailable === 0}>
          <div className="seat-header">
            <div className="seat-icon-wrapper front">
              <MdAirlineSeatReclineExtra size={22} />
            </div>
            <div className="seat-meta">
              <span className="seat-title">Front Seat</span>
              <span className="seat-price">Rs. {frontPrice.toLocaleString()}<small>/seat</small></span>
            </div>
          </div>
          <div className="seat-footer">
            <span className={`avail-tag ${frontAvailable > 0 ? "available" : "sold-out"}`}>
              {frontAvailable > 0 ? `${frontAvailable} available` : "Sold Out"}
            </span>
            <CounterPill>
              <button
                className="counter-btn"
                disabled={frontSeatsCount === 0 || totalSelectedSeats <= 1}
                onClick={handleDecreaseFront}
              >
                <FaMinus size={10} />
              </button>
              <span className="counter-value">{frontSeatsCount}</span>
              <button
                className="counter-btn"
                disabled={frontSeatsCount >= frontAvailable}
                onClick={handleIncreaseFront}
              >
                <FaPlus size={10} />
              </button>
            </CounterPill>
          </div>
        </SeatCard>

        <SeatCard $isActive={backSeatsCount > 0} $soldOut={backAvailable === 0}>
          <div className="seat-header">
            <div className="seat-icon-wrapper back">
              <MdEventSeat size={22} />
            </div>
            <div className="seat-meta">
              <span className="seat-title">Back Seats</span>
              <span className="seat-price">Rs. {backPrice.toLocaleString()}<small>/seat</small></span>
            </div>
          </div>
          <div className="seat-footer">
            <span className={`avail-tag ${backAvailable > 0 ? "available" : "sold-out"}`}>
              {backAvailable > 0 ? `${backAvailable} available` : "Sold Out"}
            </span>
            <CounterPill>
              <button
                className="counter-btn"
                disabled={backSeatsCount === 0 || totalSelectedSeats <= 1}
                onClick={handleDecreaseBack}
              >
                <FaMinus size={10} />
              </button>
              <span className="counter-value">{backSeatsCount}</span>
              <button
                className="counter-btn"
                disabled={backSeatsCount >= backAvailable}
                onClick={handleIncreaseBack}
              >
                <FaPlus size={10} />
              </button>
            </CounterPill>
          </div>
        </SeatCard>
      </SeatGrid>

      {/* Ride Details Section */}
      <SectionLabel>
        <MdLocationOn />
        Ride Details
      </SectionLabel>

      <RideDetailsCard>
        <DetailRow>
          <div className="detail-icon pickup">
            <MdMyLocation size={16} />
          </div>
          <div className="detail-content">
            <span className="detail-label">Pickup</span>
            <span className="detail-value">{ride?.leaving_from}{ride?.pickup_location ? `, ${ride?.pickup_location}` : ""}</span>
          </div>
        </DetailRow>

        <RouteLine />

        <DetailRow>
          <div className="detail-icon drop">
            <MdLocationOn size={16} />
          </div>
          <div className="detail-content">
            <span className="detail-label">Drop-off</span>
            <span className="detail-value">{ride?.heading_to}{ride?.drop_location ? `, ${ride?.drop_location}` : ""}</span>
          </div>
        </DetailRow>

        <DateTimeRow>
          <div className="dt-item">
            <MdCalendarToday size={14} />
            <span>{moment(ride?.departure_date).format("MMMM Do, YYYY")}</span>
          </div>
          <div className="dt-item">
            <MdAccessTime size={14} />
            <span>{moment(ride?.departure_time).format("h:mm A")}</span>
          </div>
        </DateTimeRow>
      </RideDetailsCard>

      {/* Seat Breakdown */}
      <SectionLabel>
        💰 Price Breakdown
      </SectionLabel>

      <BreakdownCard>
        {frontSeatsCount > 0 && (
          <BreakdownRow>
            <span className="item-label">{frontSeatsCount} × Front Seat</span>
            <span className="item-value">Rs. {(frontSeatsCount * frontPrice).toLocaleString()}</span>
          </BreakdownRow>
        )}
        {backSeatsCount > 0 && (
          <BreakdownRow>
            <span className="item-label">{backSeatsCount} × Back Seat</span>
            <span className="item-value">Rs. {(backSeatsCount * backPrice).toLocaleString()}</span>
          </BreakdownRow>
        )}

        <Divider />

        <BreakdownRow>
          <span className="item-label">
            Cash to Driver
          </span>
          <span className="item-value highlight">Rs. {driverCashPayment.toLocaleString()}</span>
        </BreakdownRow>

        <Divider $light />

        <BreakdownRow $muted>
          <span className="item-label">
            GoSawari Commission
            {configMerged.featureFlags.freeLaunch && <FreeTag>FREE</FreeTag>}
          </span>
          <span className="item-value">
            {configMerged.featureFlags.freeLaunch ? (
              <StrikeThrough>Rs. {gosawariCommission}</StrikeThrough>
            ) : (
              `Rs. ${gosawariCommission}`
            )}
            {configMerged.featureFlags.freeLaunch && " Rs. 0"}
          </span>
        </BreakdownRow>

        <BreakdownRow $muted>
          <span className="item-label">
            Service Charges
            {configMerged.featureFlags.freeLaunch && <FreeTag>FREE</FreeTag>}
          </span>
          <span className="item-value">
            {configMerged.featureFlags.freeLaunch ? (
              <StrikeThrough>Rs. {serviceCharges}</StrikeThrough>
            ) : (
              `Rs. ${serviceCharges}`
            )}
            {configMerged.featureFlags.freeLaunch && " Rs. 0"}
          </span>
        </BreakdownRow>

        <TotalRow>
          <span className="total-label">Total Platform Fee</span>
          <span className="total-value">
            Rs. {configMerged.featureFlags.freeLaunch ? "0" : totalAmountToPay}
          </span>
        </TotalRow>
      </BreakdownCard>

      {/* After Payment Info */}
      <PostPaymentInfo>
        <span className="info-icon">ℹ️</span>
        {configMerged.featureFlags.freeLaunch
          ? "After sending request"
          : "After completing your payment"}
        , you'll receive the driver's contact details on your bookings page.
      </PostPaymentInfo>

      {/* Action Buttons */}
      <ActionBar>
        <button className="back-btn" onClick={() => navigate(-1)}>
          ← Go Back
        </button>
        {configMerged.featureFlags.freeLaunch ? (
          <button
            className="primary-btn"
            onClick={() => sendFreeRequest(totalAmountToPay, seatDetailsPayload)}
            disabled={loading}
          >
            {loading ? "Sending..." : "🚀 Send Request"}
          </button>
        ) : (
          <button
            className="primary-btn pay"
            onClick={(e) => submitPayment(totalAmountToPay, seatDetailsPayload)}
            disabled={loading}
          >
            {loading ? "Processing..." : `💳 Pay Rs. ${totalAmountToPay}`}
          </button>
        )}
      </ActionBar>
    </SummaryCard>
  );
}

export default Summary;

/* ─── Animations ─── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
`;

/* ─── Styled Components ─── */

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

const SummaryCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  animation: ${fadeInUp} 0.5s ease-out;
`;

const CashBanner = styled.div`
  background: linear-gradient(135deg, #054752 0%, #006C6D 100%);
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  .cash-icon {
    font-size: 2rem;
    line-height: 1;
  }

  .cash-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .cash-label {
    font-size: 0.78rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .cash-amount {
    font-size: 1.5rem;
    font-weight: 800;
    color: #ffffff;
    letter-spacing: -0.5px;
  }

  .seats-pill {
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #ffffff;
    padding: 0.4rem 0.9rem;
    border-radius: 100px;
    font-size: 0.8rem;
    font-weight: 700;
    white-space: nowrap;
  }

  @media (max-width: 400px) {
    flex-wrap: wrap;
    .seats-pill { margin-left: auto; }
  }
`;

const InfoText = styled.p`
  margin: 0;
  padding: 1rem 1.5rem;
  font-size: 0.85rem;
  color: #475569;
  line-height: 1.6;
  background: #f8fffe;
  border-bottom: 1px solid #e2e8f0;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.88rem;
  font-weight: 700;
  color: #0f172a;
  padding: 1.25rem 1.5rem 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  svg {
    color: ${COLORS.primaryColor};
  }
`;

const SeatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  padding: 0 1.5rem 1rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const SeatCard = styled.div`
  background: ${(props) => (props.$isActive ? "rgba(5, 71, 82, 0.04)" : "#fafbfc")};
  border: 2px solid ${(props) => (props.$isActive ? COLORS.primaryColor : "#e8ecf1")};
  border-radius: 14px;
  padding: 1rem;
  transition: all 0.25s ease;
  opacity: ${(props) => (props.$soldOut ? 0.5 : 1)};

  &:hover {
    border-color: ${(props) => (!props.$soldOut ? COLORS.primaryColor : "#e8ecf1")};
    box-shadow: ${(props) => (!props.$soldOut ? "0 4px 16px rgba(5, 71, 82, 0.1)" : "none")};
  }

  .seat-header {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    margin-bottom: 0.75rem;
  }

  .seat-icon-wrapper {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &.front {
      background: linear-gradient(135deg, #054752, #006C6D);
      color: #fff;
    }
    &.back {
      background: linear-gradient(135deg, #1B937A, #10B981);
      color: #fff;
    }
  }

  .seat-meta {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .seat-title {
    font-weight: 700;
    font-size: 0.95rem;
    color: #1e293b;
  }

  .seat-price {
    font-weight: 600;
    font-size: 0.85rem;
    color: ${COLORS.primaryColor};
    small {
      font-weight: 400;
      color: #94a3b8;
      font-size: 0.7rem;
    }
  }

  .seat-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .avail-tag {
    font-size: 0.72rem;
    font-weight: 600;
    padding: 0.2rem 0.6rem;
    border-radius: 100px;

    &.available {
      background: #ecfdf5;
      color: #059669;
    }
    &.sold-out {
      background: #fef2f2;
      color: #dc2626;
    }
  }
`;

const CounterPill = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  background: #f1f5f9;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e2e8f0;

  .counter-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${COLORS.primaryColor};
    transition: all 0.15s;

    &:hover:not(:disabled) {
      background: #e2e8f0;
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }

  .counter-value {
    font-size: 1rem;
    font-weight: 800;
    color: #0f172a;
    min-width: 28px;
    text-align: center;
  }
`;

const RideDetailsCard = styled.div`
  margin: 0 1.5rem 0.5rem;
  background: #fafbfc;
  border: 1px solid #e8ecf1;
  border-radius: 14px;
  padding: 1rem 1.2rem;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;

  .detail-icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &.pickup {
      background: #ecfdf5;
      color: #059669;
    }
    &.drop {
      background: #fef2f2;
      color: #ef4444;
    }
  }

  .detail-content {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .detail-label {
    font-size: 0.72rem;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .detail-value {
    font-size: 0.9rem;
    font-weight: 600;
    color: #1e293b;
  }
`;

const RouteLine = styled.div`
  width: 2px;
  height: 16px;
  background: #e2e8f0;
  margin-left: 15px;
  border-radius: 1px;
`;

const DateTimeRow = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px dashed #e2e8f0;

  .dt-item {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.82rem;
    font-weight: 600;
    color: #475569;

    svg {
      color: ${COLORS.primaryColor};
    }
  }
`;

const BreakdownCard = styled.div`
  margin: 0 1.5rem 0.5rem;
  background: #fafbfc;
  border: 1px solid #e8ecf1;
  border-radius: 14px;
  padding: 1rem 1.2rem;
`;

const BreakdownRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0;

  .item-label {
    font-size: 0.85rem;
    font-weight: 500;
    color: ${(props) => (props.$muted ? "#94a3b8" : "#475569")};
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .item-value {
    font-size: 0.85rem;
    font-weight: 600;
    color: ${(props) => (props.$muted ? "#94a3b8" : "#1e293b")};

    &.highlight {
      font-weight: 800;
      color: ${COLORS.primaryColor};
      font-size: 0.95rem;
    }
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${(props) => (props.$light ? "#f1f5f9" : "#e2e8f0")};
  margin: 0.4rem 0;
`;

const FreeTag = styled.span`
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff;
  font-size: 0.6rem;
  font-weight: 800;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StrikeThrough = styled.span`
  text-decoration: line-through;
  opacity: 0.6;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0 0.25rem;
  margin-top: 0.4rem;
  border-top: 2px solid #e2e8f0;

  .total-label {
    font-size: 0.9rem;
    font-weight: 700;
    color: #0f172a;
  }

  .total-value {
    font-size: 1.15rem;
    font-weight: 800;
    color: ${COLORS.primaryColor};
  }
`;

const PostPaymentInfo = styled.div`
  margin: 1rem 1.5rem;
  padding: 0.85rem 1rem;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
  font-size: 0.82rem;
  color: #92400e;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;

  .info-icon {
    flex-shrink: 0;
    font-size: 1rem;
  }
`;

const ActionBar = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e8ecf1;
  background: #fafbfc;
  border-radius: 0 0 20px 20px;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
  }

  .back-btn {
    flex: 1;
    padding: 0.85rem 1.2rem;
    border: 1.5px solid #cbd5e1;
    background: transparent;
    color: #475569;
    font-weight: 600;
    font-size: 0.9rem;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #f1f5f9;
      border-color: #94a3b8;
    }
  }

  .primary-btn {
    flex: 2;
    padding: 0.85rem 1.5rem;
    border: none;
    background: linear-gradient(135deg, #054752 0%, #006C6D 100%);
    color: #ffffff;
    font-weight: 700;
    font-size: 0.95rem;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.25s ease;
    letter-spacing: 0.3px;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(5, 71, 82, 0.3);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &.pay {
      background: linear-gradient(135deg, #059669 0%, #10B981 100%);
      &:hover:not(:disabled) {
        box-shadow: 0 6px 20px rgba(5, 150, 105, 0.3);
      }
    }
  }
`;
